import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Instagram, Trophy } from "lucide-react";
import api from "../api";
import { useI18n } from "../i18n";

const About = () => {
  const { t, lang } = useI18n();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    let cancel = false;
    api
      .get("/team")
      .then((res) => !cancel && setTeam(res.data))
      .catch((e) => console.error("Failed to load team", e));
    return () => {
      cancel = true;
    };
  }, []);

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Heading + lead */}
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-end">
          <div className="col-span-12 md:col-span-5">
            <div className="kicker mb-4">{t.about.kicker}</div>
            <h2
              className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter"
              data-testid="about-title"
            >
              {t.about.title}
            </h2>
            <p className="mt-3 text-[#FFDE00] text-sm tracking-widest uppercase font-medium">
              {t.about.tagline}
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 md:pl-8 space-y-5">
            <p className="text-lg md:text-xl text-white/85 leading-relaxed font-light">
              {t.about.lead}
            </p>
            {t.about.paragraphs.map((p, i) => (
              <p key={i} className="text-base md:text-lg text-white/65 leading-relaxed font-light">
                {p}
              </p>
            ))}
          </div>
        </div>

        {/* Achievements */}
        <div className="mt-20 border border-white/15 bg-[#0a0a0a]/85 backdrop-blur-sm p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <Trophy size={18} className="text-[#FFDE00]" />
            <div className="kicker !mb-0">{t.about.achievements_title}</div>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
            {t.about.achievements.map((a, i) => (
              <li key={i} className="flex items-start gap-3 text-white/80">
                <span className="text-[#FFDE00] mt-1 text-xs font-bold">★</span>
                <span className="font-light">{a}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Members */}
        <div className="mt-20 mb-10 flex items-center gap-4">
          <span className="kicker">{t.about.members_title}</span>
          <span className="divider-line flex-1" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-testid="team-grid">
          {team.map((m, idx) => (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className="member-card group"
              data-testid={`team-member-${m.id}`}
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-[#161616]">
                <img
                  src={m.image}
                  alt={lang === "ru" ? m.name_ru : m.name_en}
                  className="member-img w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <div className="pt-4">
                <div className="font-display text-2xl tracking-tight">
                  {lang === "ru" ? m.name_ru : m.name_en}
                </div>
                <div className="text-xs tracking-widest text-[#FFDE00] uppercase mt-1">
                  {lang === "ru" ? m.role_ru : m.role_en}
                </div>
                <p className="text-sm text-white/60 mt-3 font-light leading-relaxed">
                  {lang === "ru" ? m.bio_ru : m.bio_en}
                </p>
                {m.instagram && (
                  <a
                    href={m.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-3 text-xs text-white/50 hover:text-white"
                  >
                    <Instagram size={14} /> Instagram
                  </a>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;
